import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useCallback, useEffect } from "react";
import { Loading } from "@medoma/components/Loading/Loading";
import * as Sentry from "@sentry/react";
import { deducedError } from "@/Utils/ErrorUtils";
import { useLingui } from "@lingui/react/macro";
import { clearOtherUsersSessionStorage } from "@medoma/shared-employee/userSessionStorage";

export const RequireGoAuth = ({ children }: { children: React.ReactNode }) => {
  const { t } = useLingui();
  const auth = useAuth();
  const { error, isAuthenticated, activeNavigator, isLoading, events, user } =
    auth;

  const signin = useCallback(() => {
    void auth.signinRedirect({
      url_state: user?.profile.sub,
      state: window.location.pathname,
      acr_values: "mfa",
    });
  }, [auth, user]);

  // automatically sign-in
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !isAuthenticated &&
      !activeNavigator &&
      !isLoading
    ) {
      signin();
    }
  }, [isAuthenticated, activeNavigator, isLoading, signin]);

  // Token can expire while the app is in background
  // Forcefully sign in again if this happens
  useEffect(() => {
    return events.addAccessTokenExpired(signin);
  }, [events, signin]);

  if (activeNavigator || isLoading) {
    return <Loading message={t`Verifierar behörighet`} padding={24} />;
  }

  if (isAuthenticated && user) {
    // The gate every signed-in route passes, so another employee's tab-scoped
    // storage is gone before any page reads its key.
    clearOtherUsersSessionStorage(user.profile.sub);
    return children;
  }

  if (error) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Du har inte behörighet.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  return null;
};
